export const ALIGN = { top: 'top', bottom: 'bottom' };
export const SECTION_TYPE = { cabinet: 'cabinet', shelf: 'shelf', drawer: 'drawer' };
export const SHELF_PLACEMENT = { bottom: 'bottom', center: 'center' };
export const MAX_SECTIONS = 6;
export const MAX_SECTION_QUANTITY = 12;
export const DEFAULT_SECTION_HEIGHT = 30;

const VALID_ALIGNMENTS = Object.values(ALIGN);
const VALID_SECTION_TYPES = Object.values(SECTION_TYPE);
const VALID_SHELF_PLACEMENTS = Object.values(SHELF_PLACEMENT);

let _idCounter = 0;
const makeId = () => `tci-${++_idCounter}`;

export const roundHeight = (height) => Math.round(height * 100) / 100;

export const sumSectionHeights = (sections = []) => {
  return sections.reduce((sum, section) => sum + section.height, 0);
};

const normalizeSectionQuantity = (quantity) => {
  const parsedQuantity = parseInt(quantity, 10);
  if (isNaN(parsedQuantity) || parsedQuantity < 1) {
    return 1;
  }

  return Math.min(parsedQuantity, MAX_SECTION_QUANTITY);
};

// Cabinet "quantity" means interior shelf count — allow 0 (no shelves).
const normalizeCabinetShelfCount = (quantity) => {
  const parsedQuantity = parseInt(quantity, 10);
  if (isNaN(parsedQuantity) || parsedQuantity < 0) {
    return 0;
  }

  return Math.min(parsedQuantity, MAX_SECTION_QUANTITY);
};

export const createCabinetInteriorSection = (type, height, options = {}) => {
  const section = {
    id: makeId(),
    type,
    height,
    quantity: type === SECTION_TYPE.cabinet
      ? normalizeCabinetShelfCount(options.quantity)
      : normalizeSectionQuantity(options.quantity),
  };

  if (type === SECTION_TYPE.shelf && options.placement) {
    return {
      ...section,
      placement: options.placement,
    };
  }

  return section;
};

const normalizeSection = (section) => {
  const type = VALID_SECTION_TYPES.includes(section.type) ? section.type : SECTION_TYPE.cabinet;
  const height = Math.max(1, roundHeight(Number(section.height) || DEFAULT_SECTION_HEIGHT));
  const normalizedSection = {
    id: section.id || makeId(),
    type,
    height,
    quantity: type === SECTION_TYPE.cabinet
      ? normalizeCabinetShelfCount(section.quantity)
      : normalizeSectionQuantity(section.quantity),
  };

  if (type !== SECTION_TYPE.shelf || !VALID_SHELF_PLACEMENTS.includes(section.placement)) {
    return normalizedSection;
  }

  return {
    ...normalizedSection,
    placement: section.placement,
  };
};

const fitSectionsToInteriorHeight = (sections, interiorHeight) => {
  const totalHeight = sumSectionHeights(sections);
  if (totalHeight <= interiorHeight || totalHeight <= 0) {
    return sections;
  }

  const scale = interiorHeight / totalHeight;
  let remainingHeight = interiorHeight;

  return sections.map((section, index) => {
    const isLastSection = index === sections.length - 1;
    const nextHeight = isLastSection
      ? Math.max(1, roundHeight(remainingHeight))
      : Math.max(1, roundHeight(section.height * scale));

    remainingHeight = roundHeight(remainingHeight - nextHeight);

    return {
      ...section,
      height: nextHeight,
    };
  });
};

const buildSectionsFromRatios = (glbSections, interiorHeightInches) => {
  let remainingHeight = interiorHeightInches;

  return glbSections.map((section, index) => {
    const isLastSection = index === glbSections.length - 1;
    const rawHeight = isLastSection ? remainingHeight : roundHeight(section.heightRatio * interiorHeightInches);
    const height = Math.max(1, rawHeight);

    remainingHeight = roundHeight(remainingHeight - height);

    return createCabinetInteriorSection(section.type, height, {
      quantity: section.quantity,
    });
  });
};

export const buildLayoutFromGlbSections = (glbSections, interiorHeightInches) => ({
  alignment: ALIGN.top,
  sections: buildSectionsFromRatios(glbSections, interiorHeightInches),
});

export const createDefaultCabinetInteriorLayout = (interiorHeightInches) => ({
  alignment: ALIGN.top,
  sections: [
    createCabinetInteriorSection(SECTION_TYPE.cabinet, interiorHeightInches),
  ],
});

// Stable layout contract consumed by the UI and 3D manipulator:
// valid alignment, valid section types, positive heights, and total height <= interior height.
export const normalizeLayoutForShape = (layout, interiorHeight) => {
  if (!layout || !Array.isArray(layout.sections) || layout.sections.length === 0) {
    return null;
  }

  return {
    alignment: VALID_ALIGNMENTS.includes(layout.alignment) ? layout.alignment : ALIGN.top,
    sections: fitSectionsToInteriorHeight(layout.sections.map(normalizeSection), interiorHeight),
  };
};
